import Vue from 'vue';
import widgetService from '@/services/widgetService';

const state = {
    preferences: {},
    loading: false,
    error: null,
    globalWidgetId: null,
};

const WIDGET_TYPE = 'userPreferences';

const findUserPreferencesWidget = async (widgetService) => {
    try {
        const widgets = await widgetService.getWidgetsByType(WIDGET_TYPE);
        return widgets && widgets.length > 0 ? widgets[0] : null;
    } catch (error) {
        console.warn('Failed to fetch user preferences widget:', error);
        return null;
    }
};

const normalizeTablePreferences = (tablePreferences) => {
    if (!tablePreferences || typeof tablePreferences !== 'object') {
        return {
            tables: {},
        };
    }

    const normalized = {
        tables: Array.isArray(tablePreferences.tables) ? {} : tablePreferences.tables || {},
    };

    return normalized;
};

const mergeTablePreferencesWithDefaults = (userPreferences, defaultPreferences) => {
    const merged = { ...userPreferences, tables: { ...userPreferences.tables } };

    Object.keys(defaultPreferences.tables || {}).forEach((tableId) => {
        const defaultTablePrefs = defaultPreferences.tables[tableId];
        const userTablePrefs = merged.tables[tableId] || {};

        merged.tables[tableId] = {
            ...defaultTablePrefs,
            ...userTablePrefs,
            columns: {
                ...defaultTablePrefs?.columns,
                ...userTablePrefs?.columns,
                visibility: {
                    ...defaultTablePrefs?.columns?.visibility,
                    ...userTablePrefs?.columns?.visibility,
                },
            },
        };
    });

    return merged;
};

const normalizeFeatureAnnouncements = (featureAnnouncements) => {
    if (!featureAnnouncements || typeof featureAnnouncements !== 'object') {
        return {
            seenAnnouncements: {},
            dismissedAnnouncements: [],
        };
    }

    const normalized = {
        seenAnnouncements: Array.isArray(featureAnnouncements.seenAnnouncements)
            ? {}
            : featureAnnouncements.seenAnnouncements || {},
        dismissedAnnouncements: Array.isArray(featureAnnouncements.dismissedAnnouncements)
            ? featureAnnouncements.dismissedAnnouncements
            : featureAnnouncements.dismissedAnnouncements || [],
    };

    return normalized;
};

const updateWidgetState = async (widget, stateKey, value) => {
    if (!widget) {
        console.warn('User preferences widget not found, proceeding with local state only');
        return;
    }

    try {
        await widgetService.updateWidget(widget.uuid, {
            state: {
                ...widget.state,
                [stateKey]: value,
            },
        });
    } catch (error) {
        console.warn(
            `Failed to update ${stateKey} in widget ${widget.uuid}, proceeding with local state only:`,
            error,
        );
    }
};

const hasMissingColumns = (widget, defaultPreferences) => {
    return Object.keys(defaultPreferences.state.tablePreferences.tables || {}).some((tableId) => {
        const defaultVisibility =
            defaultPreferences.state.tablePreferences.tables[tableId]?.columns?.visibility || {};
        const userVisibility =
            widget.state.tablePreferences?.tables?.[tableId]?.columns?.visibility || {};
        return Object.keys(defaultVisibility).some((colKey) => !(colKey in userVisibility));
    });
};

const mutations = {
    SET_LOADING(state, loading) {
        state.loading = loading;
    },

    SET_ERROR(state, error) {
        state.error = error;
    },

    SET_GLOBAL_PREFERENCES(state, { preferences, widgetId }) {
        state.preferences = preferences;
        if (widgetId) {
            state.globalWidgetId = widgetId;
        }
    },

    UPDATE_TABLE_PREFERENCES(state, { tableId, preferences }) {
        if (!state.preferences.tables) {
            Vue.set(state.preferences, 'tables', {});
        }

        const existingPreferences = state.preferences.tables[tableId] || {};
        const { columns, ...otherPreferences } = preferences;

        const mergedColumns = columns
            ? {
                  ...existingPreferences.columns,
                  ...columns,
                  visibility: {
                      ...existingPreferences.columns?.visibility,
                      ...columns.visibility,
                  },
              }
            : existingPreferences.columns;

        const updatedPreferences = {
            ...existingPreferences,
            ...otherPreferences,
            ...(mergedColumns && { columns: mergedColumns }),
            lastModified: new Date().toISOString(),
        };

        Vue.set(state.preferences.tables, tableId, updatedPreferences);
    },

    UPDATE_DISPLAY_PREFERENCES(state, preferences) {
        if (!state.preferences.displayPreferences) {
            Vue.set(state.preferences, 'displayPreferences', {});
        }
        Vue.set(state.preferences, 'displayPreferences', {
            ...state.preferences.displayPreferences,
            ...preferences,
        });
    },

    UPDATE_EXTERNAL_DRIVER_MAPPINGS(state, mappings) {
        if (!state.preferences.externalDriverMappings) {
            Vue.set(state.preferences, 'externalDriverMappings', {});
        }
        Vue.set(state.preferences, 'externalDriverMappings', {
            ...state.preferences.externalDriverMappings,
            ...mappings,
        });
    },
};

const actions = {
    async loadGlobalPreferences({ commit }) {
        commit('SET_LOADING', true);
        commit('SET_ERROR', null);

        try {
            const defaultPreferences = {
                name: 'User Preferences',
                type: WIDGET_TYPE,
                state: {
                    tablePreferences: {
                        tables: {
                            'shifts-table': {
                                columns: {
                                    visibility: {
                                        driver: true,
                                        sequenceNumber: true,
                                        licenseNumber: true,
                                        startAt: true,
                                        endAt: true,
                                        duration: true,
                                        dauerDez: false,
                                        totalDistance: true,
                                        hiredDistance: true,
                                        forHireDistance: true,
                                        finalExtrasAmount: true,
                                        subtractedTotalAmount: true,
                                        tips: true,
                                        cashAmount: true,
                                        finalTotalAmount: true,
                                        paymentMethod_uber: false,
                                        paymentMethod_bolt: false,
                                        paymentMethod_bliq: false,
                                        paymentMethod_freenow: false,
                                        paymentMethod_card: false,
                                        paymentMethod_coupon: false,
                                        paymentMethod_invoice: false,
                                        paymentMethod_taxieupayment: false,
                                        paymentMethod_taxideutschland: false,
                                    },
                                },
                                defaultVisible: [],
                            },
                            'trips-table': {
                                columns: {
                                    visibility: {
                                        driver: true,
                                        licenseNumber: true,
                                        shiftSequenceNumber: true,
                                        sequenceNumber: true,
                                        startAt: true,
                                        endAt: true,
                                        dauerDez: false,
                                        totalDistance: true,
                                        hiredDistance: true,
                                        onCallDistance: true,
                                        tips: true,
                                        umsatz: true,
                                        paymentMethod_uber: false,
                                        paymentMethod_bolt: false,
                                        paymentMethod_bliq: false,
                                        paymentMethod_freenow: false,
                                        paymentMethod_card: false,
                                        paymentMethod_coupon: false,
                                        paymentMethod_invoice: false,
                                        paymentMethod_taxieupayment: false,
                                        paymentMethod_taxideutschland: false,
                                    },
                                },
                                defaultVisible: [],
                            },
                            'operation-ride-car-table': {
                                columns: {
                                    visibility: {
                                        driver: true,
                                        licenseNumber: true,
                                        startAt: true,
                                        endAt: true,
                                        duration: true,
                                        mileageStart: true,
                                        mileageEnd: true,
                                        totalDistance: true,
                                        hiredDistance: true,
                                        tripsCount: true,
                                        eurPerKm: true,
                                        revenue: true,
                                    },
                                },
                                defaultVisible: [],
                            },
                            'operation-ride-table': {
                                columns: {
                                    visibility: {
                                        licenseNumber: true,
                                        kmWithoutPrivate: true,
                                        kmWithPrivate: true,
                                        privateKm: true,
                                        hiredDistance: true,
                                        emptyKm: true,
                                        revenue: true,
                                        revenuePerKm: true,
                                        tripsCount: true,
                                        shifts6hOrMore: true,
                                        occupancyRate: true,
                                    },
                                },
                                defaultVisible: [],
                            },
                        },
                    },
                    calendarPreferences: {},
                    featureAnnouncements: {
                        seenAnnouncements: {},
                        dismissedAnnouncements: [],
                    },
                    displayPreferences: {
                        hideTripMap: false,
                    },
                    externalDriverMappings: {},
                },
            };

            let widget = await findUserPreferencesWidget(widgetService);

            if (!widget) {
                widget = await widgetService.createWidget(defaultPreferences);
            }

            const rawPreferences =
                widget.state.tablePreferences || defaultPreferences.state.tablePreferences;
            const mergedRawPreferences = mergeTablePreferencesWithDefaults(
                rawPreferences,
                defaultPreferences.state.tablePreferences,
            );
            const preferences = normalizeTablePreferences(mergedRawPreferences);

            const displayPreferences = {
                ...defaultPreferences.state.displayPreferences,
                ...(widget.state.displayPreferences || {}),
            };

            const externalDriverMappings = {
                ...defaultPreferences.state.externalDriverMappings,
                ...(widget.state.externalDriverMappings || {}),
            };

            if (Object.keys(externalDriverMappings).length > 0) {
                console.log(
                    '[Driver Mappings] Loaded mappings:',
                    Object.entries(externalDriverMappings).map(([employeeId, platforms]) => ({
                        employeeId,
                        platforms: Object.entries(platforms ?? {}).map(
                            ([platform, externalId]) => ({ platform, externalId }),
                        ),
                    })),
                );
            }

            const mergedPreferences = {
                ...preferences,
                displayPreferences,
                externalDriverMappings,
            };

            commit('SET_GLOBAL_PREFERENCES', {
                preferences: mergedPreferences,
                widgetId: widget.uuid,
            });

            const needsSave =
                Array.isArray(widget.state.tablePreferences?.tables) ||
                Array.isArray(widget.state.calendarPreferences) ||
                Array.isArray(widget.state.featureAnnouncements?.seenAnnouncements) ||
                !widget.state.displayPreferences ||
                !widget.state.externalDriverMappings ||
                hasMissingColumns(widget, defaultPreferences);

            if (needsSave) {
                await widgetService.updateWidget(widget.uuid, {
                    state: {
                        ...widget.state,
                        tablePreferences: preferences,
                        calendarPreferences: Array.isArray(widget.state.calendarPreferences)
                            ? {}
                            : widget.state.calendarPreferences || {},
                        featureAnnouncements: normalizeFeatureAnnouncements(
                            widget.state.featureAnnouncements,
                        ),
                        displayPreferences,
                        externalDriverMappings,
                    },
                });
            }

            return preferences;
        } catch (error) {
            commit('SET_ERROR', error);
            throw error;
        } finally {
            commit('SET_LOADING', false);
        }
    },

    async updateTablePreferences({ commit, state }, { tableId, preferences }) {
        try {
            const currentPreferences = state.preferences || { tables: {} };
            const updatedTablePreferences = {
                ...currentPreferences,
                tables: {
                    ...currentPreferences.tables,
                    [tableId]: {
                        ...preferences,
                        lastModified: new Date().toISOString(),
                    },
                },
            };

            const widget = await findUserPreferencesWidget(widgetService);
            await updateWidgetState(widget, 'tablePreferences', updatedTablePreferences);

            commit('UPDATE_TABLE_PREFERENCES', {
                tableId,
                preferences: updatedTablePreferences.tables[tableId],
            });

            return updatedTablePreferences;
        } catch (error) {
            commit('SET_ERROR', error);
            throw error;
        }
    },

    async updateDisplayPreferences({ commit, state }, preferences) {
        try {
            const currentDisplayPreferences = state.preferences?.displayPreferences || {};
            const updatedDisplayPreferences = {
                ...currentDisplayPreferences,
                ...preferences,
            };

            const widget = await findUserPreferencesWidget(widgetService);
            await updateWidgetState(widget, 'displayPreferences', updatedDisplayPreferences);

            commit('UPDATE_DISPLAY_PREFERENCES', updatedDisplayPreferences);
            return updatedDisplayPreferences;
        } catch (error) {
            commit('SET_ERROR', error);
            throw error;
        }
    },

    async updateExternalDriverMappings({ commit, state }, { mappings, platform }) {
        try {
            const currentMappings = state.preferences?.externalDriverMappings ?? {};
            const storedFormat = {};

            Object.entries(mappings ?? {}).forEach(([externalId, employeeId]) => {
                if (employeeId) {
                    storedFormat[employeeId] = {
                        ...(currentMappings[employeeId] ?? {}),
                        [platform]: externalId,
                    };
                }
            });

            const updatedMappings = {
                ...currentMappings,
                ...storedFormat,
            };

            Object.keys(updatedMappings).forEach((employeeId) => {
                const platforms = updatedMappings[employeeId];
                if (!platforms || Object.keys(platforms).length === 0) {
                    delete updatedMappings[employeeId];
                }
            });

            const widget = await findUserPreferencesWidget(widgetService);
            await updateWidgetState(widget, 'externalDriverMappings', updatedMappings);

            commit('UPDATE_EXTERNAL_DRIVER_MAPPINGS', updatedMappings);

            console.log(
                '[Driver Mappings] Updated mappings:',
                Object.entries(updatedMappings).map(([employeeId, platforms]) => ({
                    employeeId,
                    platforms: Object.entries(platforms ?? {}).map(([platform, externalId]) => ({
                        platform,
                        externalId,
                    })),
                })),
            );

            return updatedMappings;
        } catch (error) {
            commit('SET_ERROR', error);
            throw error;
        }
    },

    async saveExternalDriverMapping({ commit, state }, { employeeId, platformIds }) {
        try {
            const currentMappings = state.preferences?.externalDriverMappings ?? {};

            const cleanedPlatformIds = {};
            Object.entries(platformIds ?? {}).forEach(([platform, externalId]) => {
                if (externalId && externalId.trim()) {
                    cleanedPlatformIds[platform] = externalId.trim();
                }
            });

            const updatedMappings = {
                ...currentMappings,
            };

            if (Object.keys(cleanedPlatformIds).length === 0) {
                delete updatedMappings[employeeId];
            } else {
                updatedMappings[employeeId] = cleanedPlatformIds;
            }

            const widget = await findUserPreferencesWidget(widgetService);
            await updateWidgetState(widget, 'externalDriverMappings', updatedMappings);

            commit('UPDATE_EXTERNAL_DRIVER_MAPPINGS', updatedMappings);

            return updatedMappings;
        } catch (error) {
            commit('SET_ERROR', error);
            throw error;
        }
    },
};

const getters = {
    getTablePreferences: (state) => (tableId) => {
        return state.preferences?.tables?.[tableId] || null;
    },

    getGlobalPreferences: (state) => {
        return state.preferences || null;
    },

    getDisplayPreferences: (state) => {
        return state.preferences?.displayPreferences || { hideTripMap: false };
    },

    getExternalDriverMappings: (state) => {
        return state.preferences?.externalDriverMappings ?? {};
    },

    getExternalDriverMappingByEmployeeId: (state) => (employeeId) => {
        return state.preferences?.externalDriverMappings?.[employeeId] ?? {};
    },

    isLoading: (state) => state.loading,
    getError: (state) => state.error,
};

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
};
